import { Box, Chip, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { useOrg } from "../hooks/userOrg";
import { useEffect, useState } from "react";
import CustomButton from "../../../core/components/ui/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from "react-router-dom";
import BookingPage from "../../../plugins/BookingPlugin/pages/booking.page";
import EmailIcon from '@mui/icons-material/Email';
import CallIcon from '@mui/icons-material/Call';
import moment from "moment";
import VehiclePage from "../../VehiclePlugin/pages/vehicle.page";
import ContainerPage from "../../ContainerPlugin/pages/container.page";
import GatePassPage from "../../GatePassPlugin/pages/gate-pass.page";
import { UserListPage } from "../../UserPlugin";
import DriverPage from "../../DriverPlugin/pages/driver.page";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  }
}

export default function OrganizationViewPage() {
  const org_uid = new URLSearchParams(window.location.search).get("org_uid");
  const { viewOrg, orgDetails, viewOrgSubscription, orgSubscriptionDetails } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔗 Tab path mapping
  const tabPaths = [
    "vehicles",
    "gatepass",
    "bookings",
    "containers",
    "users",
    "drivers"
  ];

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    navigate(`/organization/view/${tabPaths[newValue]}?org_uid=${org_uid}`);
  };

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    const tabIndex = tabPaths.indexOf(currentPath);
    if (tabIndex !== -1) {
      setValue(tabIndex);
    }
  }, [location.pathname]);

  const handleBack = () => {
    navigate("/organization");
  };

  useEffect(() => {
    if (org_uid) viewOrg(org_uid);
  }, []);

  useEffect(() => {
    if (org_uid) {
      let query_params = { org_uid: org_uid };
      viewOrgSubscription(query_params);
    }
  }, []);

  const organizationStatusStyles = {
    "Approved": { backgroundColor: '#e8f5e9', color: '#4caf50' },
    "Disapproved": { backgroundColor: '#F3F4F6', color: '#6B7280' },
    "Pending": { backgroundColor: '#FEF9C3', color: '#854D0E' },
  };

  const expirationDateFormatted = moment(orgSubscriptionDetails?.end_date).format("MMMM DD, YYYY");

  return (
    <Box>
      <CustomButton
        variant="text"
        startIcon={<ArrowBackIcon fontSize="small" />}
        label="Back to Organization"
        customStyles={{ fontSize: 14, mt: 2 }}
        onClick={handleBack}
      />

      {/* ORG HEADER */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 3, boxShadow: 2, backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {orgDetails?.org_name}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={orgDetails?.status}
            style={organizationStatusStyles[orgDetails?.status] || {}}
            size="small"
          />
        </Box>

        <Typography variant="body1" color="text.secondary" gutterBottom>
          {orgDetails?.description}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item size={{xs: 12, sm: 3}} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }} gap={1}>
              <Typography variant="body2" sx={{ color: 'gray' }}>Subscription Type: </Typography>
              <Typography variant="body2">{orgSubscriptionDetails?.type}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }} gap={1}>
              <Typography variant="body2" sx={{ color: 'gray' }}>Expiration Date: </Typography>
              <Typography variant="body2">{expirationDateFormatted}</Typography>
            </Box>
          </Grid>
          <Grid item size={{xs: 12, sm: 3}}>
            <Box sx={{ display: "flex", alignItems: "center" }} gap={1}>
              <IconButton sx={{ bgcolor: "#0b99da" }}>
                <EmailIcon sx={{ color: '#fff', height: 15, width: 15 }} />
              </IconButton>
              <Typography variant="body2">{orgDetails?.contact_email}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }} gap={1}>
              <IconButton sx={{ bgcolor: "#ff5353" }}>
                <CallIcon sx={{ color: '#fff', height: 15, width: 15 }} />
              </IconButton>
              <Typography variant="body2">{orgDetails?.contact_phone}</Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          <strong>Date Registered:</strong> {moment(orgDetails?.created_at).format("MMMM DD, YYYY")}
        </Typography>
      </Box>

      {/* TABS */}
      <Box sx={{ width: "100%", mt: 2 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="inherit"
          indicatorColor="none"
          sx={{
            display: 'inline-flex',
            bgcolor: '#F1F5F9',
            borderRadius: 2,
            p: 1,
            minHeight: '30px',
            mb: 2
          }}
          
        >
            <Tab label="Vehicles" {...a11yProps(0)}  sx={{
                bgcolor: value === 0 ? 'white' : 'transparent',
                color: value === 0 ? 'black' : 'inherit',
                '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black',
                    fontWeight:'bold',
                }, minHeight: '30px', borderRadius: 2, textTransform:"none", height: '30px'
                }}  
            />
            <Tab label="Gate Pass" {...a11yProps(1)}  sx={{
                bgcolor: value === 1 ? 'white' : 'transparent',
                color: value === 1 ? 'black' : 'inherit',
                '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black',
                    fontWeight:'bold',
                }, minHeight: '30px', borderRadius: 2, textTransform:"none", height: '30px'
                }} 
            />
            <Tab label="Bookings" {...a11yProps(2)}  sx={{
                bgcolor: value === 2 ? 'white' : 'transparent',
                color: value === 2 ? 'black' : 'inherit',
                '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black',
                    fontWeight:'bold',
                }, minHeight: '30px', borderRadius: 2, textTransform:"none", height: '30px'
                }} 
            />
            <Tab label="Containers" {...a11yProps(3)}  sx={{
                bgcolor: value === 3 ? 'white' : 'transparent',
                color: value === 3 ? 'black' : 'inherit',
                '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black',
                    fontWeight:'bold',
                }, minHeight: '30px', borderRadius: 2, textTransform:"none", height: '30px'
                }} 
            />
          <Tab label="Users" {...a11yProps(4)}  sx={{
                bgcolor: value === 4 ? 'white' : 'transparent',
                color: value === 4 ? 'black' : 'inherit',
                '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black',
                    fontWeight:'bold',
                }, minHeight: '30px', borderRadius: 2, textTransform:"none", height: '30px'
                }} 
            />
          <Tab label="Drivers" {...a11yProps(5)}  sx={{
                bgcolor: value === 5 ? 'white' : 'transparent',
                color: value === 5 ? 'black' : 'inherit',
                '&.Mui-selected': {
                    bgcolor: 'white',
                    color: 'black',
                    fontWeight:'bold',
                }, minHeight: '30px', borderRadius: 2, textTransform:"none", height: '30px'
                }} 
            />
        </Tabs>

        {/* PANELS */}
        <TabPanel value={value} index={0}><VehiclePage /></TabPanel>
        <TabPanel value={value} index={1}><GatePassPage /></TabPanel>
        <TabPanel value={value} index={2}><BookingPage /></TabPanel>
        <TabPanel value={value} index={3}><ContainerPage /></TabPanel>
        <TabPanel value={value} index={4}><UserListPage /></TabPanel>
        <TabPanel value={value} index={5}><DriverPage /></TabPanel>
      </Box>
    </Box>
  );
}
